import { useRouter } from '@tanstack/react-router'
import { type RcFile } from 'antd/es/upload'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import {
  ERROR_MESSAGES,
  SHIPMENT_ORDER_CSV_IMPORT,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  createShipmentOrderImportJob,
  createSuntoryDedicatedShipmentOrderImportJob
} from '@/features/shipment-order-import-jobs/api'
import { uploadFile } from '@/features/uploads/api'
import { type UploadFileResult } from '@/features/uploads/models'
import {
  type CutOutFileDto,
  type ShipmentOrderImportValidationEntity
} from '@/libs/api'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { validateShipmentOrderImport } from '../../api'
import {
  buildSuntoryChanges,
  buildSuntoryImportedRows,
  LOGI_FIELDS,
  type LogiFieldValue
} from '../../models'
import { csvRowsToFile, downloadCsvRows, readCsvFileRaw } from '../../utils'

/** サントリー様の CSV の種類。社内移動は標準の取込、得意先は専用の取込で取り込む（経路・パーサーは既存のまま）。 */
export type SuntoryImportKind = 'internal' | 'dedicated'

export type SuntoryImportStage = 'file' | 'preview'

export type SuntoryCheck =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'failed' }
  | {
      status: 'done'
      result: ShipmentOrderImportValidationEntity
      /** この結果を出したときの直した値（今の直した値と同じ参照なら、結果は今の値のもの） */
      checkedOverrides: SuntoryOverrides
    }

/**
 * 確認画面で直せる項目。マスタに無いエラー（荷主・配送種別・発着地・荷姿・車種・パートナー）を直すためのもの
 * （BE の SUNTORY_EDITABLE_FIELDS と同じ）。読み取りの段階で落ちた行は中身が無いので直せない（ファイルを直す）。
 */
export const SUNTORY_EDITABLE_FIELDS: ReadonlySet<LogiFieldValue> = new Set([
  'customerId',
  'shipmentCategoryId',
  'originSiteId',
  'destSiteId',
  'packageCategoryId',
  'truckModelId',
  'partnerId'
])

/** 行番号（CSV の行番号）→ 項目 → 直した値 */
export type SuntoryOverrides = Record<
  string,
  Partial<Record<LogiFieldValue, string>>
>

/** 値を直したあと、事前チェックをやり直すまで待つ時間（続けて直したときにまとめて確かめる） */
const RECHECK_DELAY_MS = 1200

const NO_OVERRIDES: SuntoryOverrides = {}

const SOURCE_OF_KIND = {
  internal: 'SUNTORY_INTERNAL',
  dedicated: 'SUNTORY_DEDICATED'
} as const

/**
 * サントリー様の CSV を、取り込む前に確認画面で見てから取り込む。
 * ファイルは画面では読まず、事前チェック API にサントリー様の読み方（source）で読ませて、行と問題を受け取る。
 * マスタに無いエラーのセルは画面で直せて、直した値で確かめ直してから取り込む（直した値は取込にも渡す）。
 * 読み取りの段階で落ちた行など、直せない問題が 1 件でもあれば取り込めない（直すのはファイル側）。
 */
export const useSuntoryImport = ({
  tenantKey,
  kind,
  onDone
}: {
  tenantKey: string
  /** サントリー様の選択肢を選んでいないときは null。 */
  kind: SuntoryImportKind | null
  onDone: () => void
}) => {
  const router = useRouter()
  const dcId = useAtomValue(selectedDcIdAtom)
  const [uploaded, setUploaded] = useState<UploadFileResult | null>(null)
  // 選んだファイルの本体。修正を反映した CSV を元の CSV と同じ形で書き出すときに読む（アップロード先からは読み直さない）
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [stage, setStage] = useState<SuntoryImportStage>('file')
  const [check, setCheck] = useState<SuntoryCheck>({ status: 'idle' })
  const [isExecuting, setIsExecuting] = useState(false)
  const [overrides, setOverrides] = useState<SuntoryOverrides>(NO_OVERRIDES)
  const [isRechecking, setIsRechecking] = useState(false)
  // 直した値が無い状態で確かめた結果の行。修正箇所の一覧の「修正前」の値に使う
  const [baseRows, setBaseRows] = useState<
    NonNullable<ShipmentOrderImportValidationEntity['rows']>
  >([])
  const recheckAbortRef = useRef<AbortController | null>(null)

  // 種類を選び直したら、選んだファイルと確認結果は持ち越さない（読み方が違うため）
  useEffect(() => {
    setUploaded(null)
    setSourceFile(null)
    setStage('file')
    setCheck({ status: 'idle' })
    setOverrides(NO_OVERRIDES)
    setBaseRows([])
  }, [kind])

  const onChangeUploadFile = useCallback((result: UploadFileResult | null) => {
    setUploaded(result)
    if (result === null) setSourceFile(null)
    setCheck({ status: 'idle' })
    setOverrides(NO_OVERRIDES)
    setBaseRows([])
  }, [])

  const validate = useCallback(
    async (target: SuntoryOverrides, signal?: AbortSignal) => {
      if (kind === null || !uploaded || dcId === null) return null
      return validateShipmentOrderImport(
        tenantKey,
        {
          fileKey: uploaded.key,
          dcId,
          source: SOURCE_OF_KIND[kind],
          ...(Object.keys(target).length > 0 ? { overrides: target } : {})
        },
        signal
      )
    },
    [kind, uploaded, dcId, tenantKey]
  )

  const canLeaveFile = kind !== null && uploaded !== null && dcId !== null

  const onLeaveFile = useCallback(async () => {
    if (kind === null || !uploaded || dcId === null) return
    setStage('preview')
    setCheck({ status: 'checking' })
    try {
      const result = await validate(overrides)
      if (result) {
        setCheck({ status: 'done', result, checkedOverrides: overrides })
        if (Object.keys(overrides).length === 0) setBaseRows(result.rows ?? [])
      }
    } catch (error) {
      setCheck({ status: 'failed' })
      toastApiErrorMessage(
        error,
        ERROR_MESSAGES.FAILED_TO_IMPORT_SHIPMENT_ORDER
      )
    }
  }, [kind, uploaded, dcId, validate, overrides])

  // 直した値で確かめ直す。結果が届くまでは前の結果を出したまま、取り込み実行は押せない
  const recheck = useCallback(
    async (target: SuntoryOverrides) => {
      recheckAbortRef.current?.abort()
      const controller = new AbortController()
      recheckAbortRef.current = controller
      setIsRechecking(true)
      try {
        const result = await validate(target, controller.signal)
        if (controller.signal.aborted || !result) return
        setCheck({ status: 'done', result, checkedOverrides: target })
      } catch (error) {
        if (controller.signal.aborted) return
        toastApiErrorMessage(
          error,
          ERROR_MESSAGES.FAILED_TO_IMPORT_SHIPMENT_ORDER
        )
      } finally {
        if (!controller.signal.aborted) setIsRechecking(false)
      }
    },
    [validate]
  )

  useEffect(() => {
    if (stage !== 'preview' || check.status !== 'done') return
    if (check.checkedOverrides === overrides) return
    const timer = setTimeout(() => void recheck(overrides), RECHECK_DELAY_MS)
    return () => clearTimeout(timer)
  }, [stage, check, overrides, recheck])
  useEffect(() => () => recheckAbortRef.current?.abort(), [])

  const onEditCell = useCallback(
    (rowNo: number, field: LogiFieldValue, value: string) => {
      if (!SUNTORY_EDITABLE_FIELDS.has(field)) return
      setOverrides((prev) => ({
        ...prev,
        [rowNo]: { ...prev[rowNo], [field]: value }
      }))
    },
    []
  )

  // 戻るとファイル欄は作り直されて選んだファイルの表示が消えるため、選び直してもらう
  const onBack = useCallback(() => {
    recheckAbortRef.current?.abort()
    setIsRechecking(false)
    setStage('file')
    setUploaded(null)
    setCheck({ status: 'idle' })
    setOverrides(NO_OVERRIDES)
    setBaseRows([])
  }, [])

  const changes = useMemo(
    () => buildSuntoryChanges(baseRows, overrides),
    [baseRows, overrides]
  )

  // 修正箇所の一覧の CSV の行（確認画面からのダウンロードと、インポート履歴に残すアップロードで共用。どちらもサントリー様の CSV と同じ Shift_JIS で書き出す）
  const changesRows = useMemo(() => {
    const labelOf = new Map(LOGI_FIELDS.map((f) => [f.value, f.label]))
    return [
      [...SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_CHANGES_HEADERS],
      ...changes.map((c) => [
        String(c.rowNo),
        c.detailId ?? '',
        labelOf.get(c.field) ?? c.field,
        c.before,
        c.after
      ])
    ]
  }, [changes])

  const onDownloadChanges = useCallback(() => {
    if (!uploaded || changes.length === 0) return
    downloadCsvRows(
      `${uploaded.name.replace(/\.[^.]+$/, '')}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX.changes}.csv`,
      changesRows,
      'SJIS'
    )
  }, [uploaded, changes, changesRows])

  // 今の直した値で確かめた結果に問題が無いときだけ取り込める（確認画面の結果と取込の結果を一致させる）
  const canExecute =
    check.status === 'done' &&
    check.checkedOverrides === overrides &&
    !isRechecking &&
    check.result.issues.length === 0 &&
    (check.result.rows?.length ?? 0) > 0

  // 修正を反映した CSV の行（元の CSV の全行に、荷主・発地・着地の直しを書き戻したもの）と文字コード。
  // 選んだファイルを読み直して作る（確認画面からのダウンロードと、インポート履歴に残すアップロードで共用）
  const readImportedRows = useCallback(async () => {
    if (!sourceFile || kind === null) return null
    const { rows, encodingCode } = await readCsvFileRaw(sourceFile)
    return {
      rows: buildSuntoryImportedRows({ kind, csvRows: rows, changes }),
      encodingCode
    }
  }, [sourceFile, kind, changes])

  // 直したところがあれば、修正を反映した CSV と修正箇所の一覧をインポート履歴に残す（#5737）。
  // 置けなくても取り込みは止めない
  const uploadCutOutFiles = useCallback(async (): Promise<
    CutOutFileDto[] | undefined
  > => {
    if (!uploaded || changes.length === 0) return undefined
    try {
      const imported = await readImportedRows()
      if (!imported) return undefined
      const baseName = uploaded.name.replace(/\.[^.]+$/, '')
      const [importedFile, changesFile] = await Promise.all([
        uploadFile(
          csvRowsToFile(
            `${baseName}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX.imported}.csv`,
            imported.rows,
            imported.encodingCode
          ) as RcFile
        ),
        uploadFile(
          csvRowsToFile(
            `${baseName}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX.changes}.csv`,
            changesRows,
            'SJIS'
          ) as RcFile
        )
      ])
      return [
        { kind: 'IMPORTED', key: importedFile.key, name: importedFile.name },
        { kind: 'CHANGES', key: changesFile.key, name: changesFile.name }
      ]
    } catch (error) {
      toastApiErrorMessage(
        error,
        SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_UPLOAD_FAILED
      )
      return undefined
    }
  }, [uploaded, changes, readImportedRows, changesRows])

  const onExecute = useCallback(async () => {
    if (!canExecute || kind === null || !uploaded || dcId === null) return
    setIsExecuting(true)
    const cutOutFiles = await uploadCutOutFiles()
    const dto = {
      fileKey: uploaded.key,
      fileName: uploaded.name,
      dcId,
      ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
      ...(cutOutFiles ? { cutOutFiles } : {})
    }
    try {
      if (kind === 'dedicated') {
        await createSuntoryDedicatedShipmentOrderImportJob({ tenantKey, dto })
        toast.success(
          SUCCESS_MESSAGES.STARTED_IMPORTING_SUNTORY_DEDICATED_SHIPMENT_ORDER
        )
      } else {
        await createShipmentOrderImportJob({ tenantKey, dto })
        toast.success(SUCCESS_MESSAGES.STARTED_IMPORTING_SHIPMENT_ORDER)
      }
    } catch (error) {
      toastApiErrorMessage(
        error,
        kind === 'dedicated'
          ? ERROR_MESSAGES.FAILED_TO_IMPORT_SUNTORY_DEDICATED_SHIPMENT_ORDER
          : ERROR_MESSAGES.FAILED_TO_IMPORT_SHIPMENT_ORDER
      )
      return
    } finally {
      setIsExecuting(false)
    }
    // 既存の取り込みと同じく、取り込みを始めたら取込履歴へ移る
    onDone()
    void router.navigate({
      to: '/$tenantKey/shipment-order-import-jobs',
      params: { tenantKey }
    })
  }, [
    canExecute,
    kind,
    uploaded,
    dcId,
    overrides,
    uploadCutOutFiles,
    tenantKey,
    onDone,
    router
  ])

  // 修正を反映した CSV を、元の CSV と同じ文字コードで書き出す
  const onDownloadImported = useCallback(async () => {
    if (!uploaded || changes.length === 0) return
    try {
      const imported = await readImportedRows()
      if (!imported) return
      downloadCsvRows(
        `${uploaded.name.replace(/\.[^.]+$/, '')}${SHIPMENT_ORDER_CSV_IMPORT.CUT_OUT_FILE_SUFFIX.imported}.csv`,
        imported.rows,
        imported.encodingCode
      )
    } catch (error) {
      toastApiErrorMessage(
        error,
        SHIPMENT_ORDER_CSV_IMPORT.SUNTORY_DOWNLOAD_IMPORTED_FAILED
      )
    }
  }, [uploaded, changes, readImportedRows])

  return {
    onSelectFile: setSourceFile,
    uploaded,
    stage,
    check,
    overrides,
    isRechecking,
    isExecuting,
    canLeaveFile,
    canExecute,
    onChangeUploadFile,
    onLeaveFile,
    onBack,
    onEditCell,
    onExecute,
    changeCount: changes.length,
    onDownloadChanges,
    canDownloadImported: sourceFile !== null && changes.length > 0,
    onDownloadImported
  }
}

export type UseSuntoryImportResult = ReturnType<typeof useSuntoryImport>
